import React from 'react';
import { FormattedMessage } from 'react-intl';
import DocumentTitle from 'react-document-title';
import { injectIntl } from 'react-intl';

// Custom Components
import { CheckIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  Button,
  Switch,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  TableHeader,
  Table,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import ComplianceOverviewTabs from './ComplianceOverviewTabs';
import ComplianceOverviewLegend from './ComplianceOverviewLegend';
import ModalCertificationHistory from './modal.CertificationHistory';
import TableLayout from '../../components/TableLayout';
import PlanningRangeFilter from '../../components/PlanningRangeFilter';

// Hooks
import { useComplianceOverview } from './hooksV2';

// Constants
import messages from './messages';
import { COMPLIANCE_TYPES } from './constants';
import useBaseTableProps from '../../hooks/useBaseTableProps';

const ComplianceOverview = ({ intl, isActive = true }) => {
  const baseTableProps = useBaseTableProps();
  const {
    allColumnsHidden,
    columnOptions,
    complianceType,
    count,
    countName,
    currentPage,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleDownloadCSVPDF,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    isLoading,
    isRefreshing,
    isBusy,
    isCSVPDFExportLoading,
    ModalProps,
    name,
    onPageChange,
    order,
    orderBy,
    pageCount,
    perPage,
    rows,
    searchState,
    selectedColumns,
    setComplianceType,
    totalCount,
    isCertStatusVisible,
    toggleCertStatusColumn,
    shouldNotStartedCertsButtonBeDisplayed,
    planningRangeOptions,
    selectedPlanningRange,
    onPlanningRangeChange,
    shouldPlanningRangeFilterBeDisplayed,
    activeFilterCount,
    handleClearFilters,
  } = useComplianceOverview({ intl, isActive });

  return (
    <DocumentTitle
      data-testid={name}
      title={intl.formatMessage(messages.title)}
    >
      <>
        <TableLayout
          name={name}
          childrenElementsHeight={113 + 8 + 20}
          containerElementsHeight={49} // tabs
        >
          {(tableHeight) => (
            <>
              <TableHeader
                title={intl.formatMessage(messages.title)}
                // All Open Certs' pager already reads "Viewing items x-y of N".
                {...(complianceType === COMPLIANCE_TYPES.NEEDS_REVIEW
                  ? { count, countName, totalCount }
                  : {})}
                Tabs={
                  <ComplianceOverviewTabs
                    complianceType={complianceType}
                    setComplianceType={setComplianceType}
                  />
                }
              />
              <ComplianceOverviewLegend complianceType={complianceType} />
              {shouldNotStartedCertsButtonBeDisplayed &&
                complianceType === COMPLIANCE_TYPES.ALL_OPEN_CERTS && (
                  <Switch
                    sx={{ paddingX: 1, paddingY: 2 }}
                    checked={isCertStatusVisible}
                    onChange={() => toggleCertStatusColumn()}
                    label={
                      <FormattedMessage
                        {...messages.includeNotStartedCertifications}
                      />
                    }
                    icon={<CheckIcon />}
                    checkedIcon={<CheckIcon />}
                    disabled={isLoading}
                  />
                )}
              {shouldPlanningRangeFilterBeDisplayed && (
                <PlanningRangeFilter
                  options={planningRangeOptions}
                  value={selectedPlanningRange}
                  onChange={onPlanningRangeChange}
                  disabled={isBusy}
                />
              )}
              <Table
                {...baseTableProps}
                allColumnsHidden={allColumnsHidden}
                columnOptions={columnOptions}
                dateState={dateState}
                filterState={filterState}
                filterTypeState={filterTypeState}
                handleColumnChange={handleColumnChange}
                handleDateSubmit={handleDateSubmit}
                handleFilterChange={handleFilterChange}
                handleFilterTypeChange={handleFilterTypeChange}
                handleSearchSubmit={handleSearchSubmit}
                handleSortChange={handleSortChange}
                headers={filteredHeaders}
                height={tableHeight}
                isLoading={isLoading || isRefreshing}
                onCSVButtonClick={() => handleDownloadCSVPDF('csv')}
                onPDFButtonClick={() => handleDownloadCSVPDF('pdf')}
                isCSVExportLoading={isCSVPDFExportLoading}
                isPDFExportLoading={isCSVPDFExportLoading}
                name={name}
                currentPage={currentPage}
                onPageChange={onPageChange}
                order={order}
                orderBy={orderBy}
                pageCount={pageCount}
                perPage={perPage}
                rows={rows}
                count={count}
                searchState={searchState}
                selectedColumns={selectedColumns}
                totalCount={totalCount}
                // Must stay inside the Table's toolbar, not a sibling above it —
                // TableLayout ignores childList mutations, so a later-mounted
                // sibling isn't subtracted from the table height.
                toolbarActions={
                  complianceType === COMPLIANCE_TYPES.ALL_OPEN_CERTS &&
                  activeFilterCount > 0 ? (
                    <Button
                      variant="primarySubtle"
                      size="small"
                      onClick={handleClearFilters}
                      data-testid="compliance-overview-clear-filters"
                    >
                      <FormattedMessage
                        {...messages.clearFilters}
                        values={{ count: activeFilterCount }}
                      />
                    </Button>
                  ) : undefined
                }
              />
            </>
          )}
        </TableLayout>
        <Modal {...ModalProps}>
          {ModalProps?.modalType === 'certificationHistory' ? (
            <ModalCertificationHistory
              {...ModalProps?.modalState}
              intl={intl}
            />
          ) : null}
        </Modal>
      </>
    </DocumentTitle>
  );
};

export default injectIntl(ComplianceOverview);
